import type { AprobacionVista, TipoAprobacion } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { BadgeCheck, CalendarClock, Check, Lock, Percent, RotateCcw, Tag, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Pestana, PestanaContenido, Pestanas, PestanasLista, Tarjeta, Vacio } from '@/components/ui/data';
import { Campo, Textarea } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { cn, fechaHora } from '@/lib/utils';
import { avisarError } from '@/lib/avisos';

const ICONOS: Record<TipoAprobacion, ReactNode> = {
  desbloqueo_caja: <Lock />,
  cambio_margen: <Percent />,
  devolucion: <RotateCcw />,
  promocion: <Tag />,
  asignacion_vacaciones: <CalendarClock />,
};
const ESTADO = {
  pendiente: { texto: 'Pendiente', tono: 'aviso' },
  aprobada: { texto: 'Aprobada', tono: 'exito' },
  rechazada: { texto: 'Rechazada', tono: 'peligro' },
  expirada: { texto: 'Expirada', tono: 'neutro' },
} as const;

export function AprobacionesPage() {
  const qc = useQueryClient();
  const { id: resaltada } = useSearch({ strict: false }) as { id?: string };
  const pendientes = useQuery({ queryKey: ['aprobaciones', 'pendiente'], queryFn: () => api<AprobacionVista[]>('/aprobaciones', { query: { estado: 'pendiente' } }) });
  const historial = useQuery({ queryKey: ['aprobaciones', 'todas'], queryFn: () => api<AprobacionVista[]>('/aprobaciones') });
  const [rechazo, setRechazo] = useState<AprobacionVista | null>(null);
  const [nota, setNota] = useState('');

  const resolver = useMutation({
    mutationFn: (v: { id: string; decision: 'aprobar' | 'rechazar'; nota?: string }) =>
      api(`/aprobaciones/${v.id}/resolver`, { method: 'POST', body: { decision: v.decision, nota: v.nota || undefined } }),
    onSuccess: (_, v) => {
      toast.success(v.decision === 'aprobar' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      setRechazo(null);
      setNota('');
      void qc.invalidateQueries({ queryKey: ['aprobaciones'] });
    },
    onError: (e) => {
      avisarError(e);
      void qc.invalidateQueries({ queryKey: ['aprobaciones'] });
    },
  });

  const tarjeta = (a: AprobacionVista, acciones: boolean) => (
    <Tarjeta key={a.id} className={cn('flex flex-wrap items-start gap-4 p-4', a.id === resaltada && 'border-accent ring-2 ring-accent/25')}>
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent-soft text-accent [&_svg]:size-[18px]">{ICONOS[a.tipo]}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium">{a.titulo}</p>
          {!acciones && <Insignia tono={ESTADO[a.estado].tono}>{ESTADO[a.estado].texto}</Insignia>}
        </div>
        {a.descripcion && <p className="mt-0.5 text-[14px] text-foreground-muted">{a.descripcion}</p>}
        <p className="mt-1.5 text-[12.5px] text-foreground-muted">
          Pidió {a.solicitante} · {fechaHora(a.createdAt)}
          {a.resueltaPor && ` · Resolvió ${a.resueltaPor}`}
          {a.nota && ` · «${a.nota}»`}
        </p>
      </div>
      {acciones && (
        <div className="flex gap-2 max-sm:w-full">
          <Button variante="secundario" onClick={() => setRechazo(a)} disabled={resolver.isPending} className="max-sm:flex-1">
            <X /> Rechazar
          </Button>
          <Button onClick={() => resolver.mutate({ id: a.id, decision: 'aprobar' })} cargando={resolver.isPending && resolver.variables?.id === a.id && resolver.variables.decision === 'aprobar'} className="max-sm:flex-1">
            <Check /> Aprobar
          </Button>
        </div>
      )}
    </Tarjeta>
  );

  return (
    <ModuleLayout titulo="Aprobaciones">
      <EncabezadoPagina titulo="Aprobaciones" descripcion="Solicitudes que requieren tu autorización: desbloqueos de caja, devoluciones, cambios de ganancia, promociones y horarios en vacaciones." />
      <Pestanas defaultValue="pendientes">
        <PestanasLista>
          <Pestana value="pendientes">Pendientes {pendientes.data?.length ? `(${pendientes.data.length})` : ''}</Pestana>
          <Pestana value="historial">Historial</Pestana>
        </PestanasLista>
        <PestanaContenido value="pendientes" className="mt-4 grid gap-3">
          {pendientes.isLoading ? (
            <Esqueleto className="h-24" />
          ) : !pendientes.data?.length ? (
            <Tarjeta>
              <Vacio icono={<BadgeCheck />} titulo="Nada pendiente" descripcion="Cuando alguien pida tu autorización aparecerá aquí y te llegará una notificación." />
            </Tarjeta>
          ) : (
            pendientes.data.map((a) => tarjeta(a, true))
          )}
        </PestanaContenido>
        <PestanaContenido value="historial" className="mt-4 grid gap-3">
          {historial.isLoading ? <Esqueleto className="h-24" /> : historial.data?.filter((a) => a.estado !== 'pendiente').map((a) => tarjeta(a, false))}
        </PestanaContenido>
      </Pestanas>

      {rechazo && (
        <Dialogo
          abierto
          ancho="sm"
          onCambiarAbierto={(v) => !v && setRechazo(null)}
          titulo="Rechazar solicitud"
          descripcion={rechazo.titulo}
          pie={
            <Button variante="peligro" onClick={() => resolver.mutate({ id: rechazo.id, decision: 'rechazar', nota })} cargando={resolver.isPending}>
              Rechazar
            </Button>
          }
        >
          <Campo etiqueta="Motivo (opcional)" ayuda="Se le enviará a quien hizo la solicitud.">
            {(p) => <Textarea {...p} value={nota} maxLength={300} onChange={(e) => setNota(e.target.value)} />}
          </Campo>
        </Dialogo>
      )}
    </ModuleLayout>
  );
}
